import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

const NavigationGuardContext = createContext(null);

export const NavigationGuardProvider = ({ children }) => {
  const [isBlocking, setIsBlocking] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  
  // Refs per callbacks
  const onSaveRef = useRef(null);
  const onConfirmRef = useRef(null);

  // Registra un guard (chiamato dalla pagina che vuole bloccare la navigazione)
  const registerGuard = useCallback(({ onSave, onConfirm }) => {
    onSaveRef.current = onSave;
    onConfirmRef.current = onConfirm;
  }, []);

  // Deregistra il guard
  const unregisterGuard = useCallback(() => {
    onSaveRef.current = null;
    onConfirmRef.current = null;
    setIsBlocking(false);
  }, []);

  // Imposta se il blocco è attivo
  const setBlocking = useCallback((blocking) => {
    setIsBlocking(blocking);
  }, []);

  // Tentativo di navigazione - ritorna true se possiamo navigare, false se bloccato
  const attemptNavigation = useCallback((path) => {
    if (isBlocking) {
      setPendingPath(path);
      setShowDialog(true);
      return false;
    }
    return true;
  }, [isBlocking]);

  // Conferma navigazione senza salvare
  const confirmNavigation = useCallback(() => {
    setShowDialog(false);
    setIsBlocking(false);
    if (onConfirmRef.current && pendingPath) {
      onConfirmRef.current(pendingPath);
    }
    setPendingPath(null);
  }, [pendingPath]);

  // Salva e naviga
  const saveAndNavigate = useCallback(async () => {
    if (onSaveRef.current) {
      await onSaveRef.current();
    }
    setShowDialog(false);
    setIsBlocking(false);
    if (onConfirmRef.current && pendingPath) {
      onConfirmRef.current(pendingPath);
    }
    setPendingPath(null);
  }, [pendingPath]);

  // Annulla navigazione
  const cancelNavigation = useCallback(() => {
    setShowDialog(false);
    setPendingPath(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isBlocking,
    showDialog,
    pendingPath,
    registerGuard,
    unregisterGuard,
    setBlocking,
    attemptNavigation,
    confirmNavigation,
    saveAndNavigate,
    cancelNavigation,
  }), [
    isBlocking,
    showDialog,
    pendingPath,
    registerGuard,
    unregisterGuard,
    setBlocking,
    attemptNavigation,
    confirmNavigation,
    saveAndNavigate,
    cancelNavigation,
  ]);

  return (
    <NavigationGuardContext.Provider value={contextValue}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = () => {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
  }
  return context;
};
